import Layout from '@/components/layout'
import { SignedIn, SignedOut } from '@clerk/clerk-react'
import { Button } from '@/components/ui/button'
import { Link } from 'react-router-dom'
import { useAuth } from '@/contexts/AuthProvider'
import { useMemo, useState } from 'react'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'

const GLOSSARY: Record<string, string> = {
  Aff: 'Affirmative. The debater arguing that the resolution should be affirmed.',
  Neg: 'Negative. The debater arguing that the resolution should not be affirmed.',
  Framework:
    'The judge’s decision lens. In LD this is often a Value + Criterion/Standard that tells the judge how to evaluate impacts.',
  Value:
    'The moral goal (e.g., Justice, Liberty, Welfare). It answers: “What should we prioritize morally?”',
  Criterion:
    'Also called a Standard. The test for achieving the Value—how the judge should measure which side is better.',
  Standard:
    'Another word for Criterion. The metric the judge uses to evaluate impacts under the Value.',
  Contention:
    'A main argument in a case. Usually written as a claim/tag + warrants + impacts tied back to the framework.',
  Warrant: 'The reasoning (and/or evidence) that proves a claim is true.',
  Impact:
    'Why an argument matters—the consequence. Impacts are compared using weighing.',
  Weighing:
    'Comparing impacts (magnitude, probability, timeframe, moral priority) to decide which matters more.',
  Offense:
    'Reasons you win—arguments that, if true, justify a ballot for your side.',
  Defense:
    'Reasons the opponent’s offense doesn’t apply or doesn’t matter (no link, weak impact, etc.).',
  Flow:
    'Structured note-taking where you track arguments and responses in columns across speeches.',
  Flowing: 'Doing a flow—writing structured notes to track arguments over the round.',
  Extension:
    'Carrying an argument forward by referencing it and re-explaining the warrant + impact so it stays “alive” on the flow.',
  Drop:
    'An argument left unanswered. Many judges treat dropped arguments as conceded.',
  Collapse:
    'Strategically narrowing to the few issues you will “go for” at the end of the round.',
  Roadmap:
    'A quick navigation sentence that tells the judge the order you’ll cover issues. Not an argument.',
  'Cross-ex':
    'Cross-examination. The question period between speeches used to clarify and set up later arguments.',
  Ballot: 'The judge’s decision form: winner + speaker points + an RFD (Reason for Decision).',
  RFD: 'Reason for Decision. The judge’s written explanation of why they voted the way they did.',
  ROB: 'Role of the Ballot. In some debates (especially kritiks), a claim about what the ballot is supposed to do.',
  Topicality:
    'A procedural argument that the opponent does not meet the proper interpretation of the resolution (common in policy; sometimes in LD).',
  Theory:
    'A procedural fairness/education argument about debate rules or norms (e.g., disclosure, abusive strategies).',
  Kritik:
    'A critical argument that challenges assumptions, language, power, or worldview behind a position; often proposes an alternative.',
  DA: 'Disadvantage. A negative position claiming the affirmative causes a harmful outcome.',
  CP: 'Counterplan. A negative alternative action/advocacy offered as better than the affirmative.',
  'On-case': 'Arguments that directly answer the opponent’s case.',
  'Off-case': 'Independent positions not on the opponent’s case (e.g., theory, kritik, DA, CP).',
}

function GlossaryTerm({
  term,
  children,
}: {
  term: keyof typeof GLOSSARY | string
  children?: React.ReactNode
}) {
  const def = GLOSSARY[String(term)]
  if (!def) return <>{children ?? term}</>

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span
          className="underline decoration-dotted underline-offset-4 cursor-help text-foreground/90 hover:text-foreground transition-colors"
          title={def}
        >
          {children ?? term}
        </span>
      </TooltipTrigger>
      <TooltipContent className="max-w-xs leading-6">{def}</TooltipContent>
    </Tooltip>
  )
}

function HistoryOfDebateArticle() {
  const images = {
    // Public links (Wikimedia Commons)
    athensPnyx: 'https://upload.wikimedia.org/wikipedia/commons/thumb/5/52/On_the_Pnyx_Hill.jpg/1280px-On_the_Pnyx_Hill.jpg',
    disputation:
      'https://upload.wikimedia.org/wikipedia/commons/thumb/9/90/Disputatio_contra_scholasticam_theologiam_1.jpg/960px-Disputatio_contra_scholasticam_theologiam_1.jpg',
    wormsLuther: 'https://upload.wikimedia.org/wikipedia/commons/4/43/Luther_at_the_Diet_of_Worms.jpg',
    lincolnDouglas:
      'https://upload.wikimedia.org/wikipedia/commons/thumb/a/a9/The_Lincoln-Douglas_debates_of_1858_%281908%29_%2814586661868%29.jpg/1280px-The_Lincoln-Douglas_debates_of_1858_%281908%29_%2814586661868%29.jpg',
    modernDebate: 'https://upload.wikimedia.org/wikipedia/commons/4/42/Vienna_Debate_Workshop-Finale.jpg',
  }

  const Pill = ({
    kind,
    label,
    value,
  }: {
    kind: 'where' | 'when' | 'idea'
    label: string
    value: string
  }) => {
    const styles =
      kind === 'where'
        ? 'bg-emerald-600/15 text-emerald-700 dark:text-emerald-300 border-emerald-600/25'
        : kind === 'when'
          ? 'bg-sky-600/15 text-sky-700 dark:text-sky-300 border-sky-600/25'
          : 'bg-violet-600/15 text-violet-700 dark:text-violet-300 border-violet-600/25'

    return (
      <span className={`inline-flex items-center gap-2 text-xs px-2 py-1 rounded-full border ${styles}`}>
        <span className="font-semibold">{label}</span>
        <span className="opacity-90">{value}</span>
      </span>
    )
  }

  const Era = ({
    title,
    where,
    when,
    idea,
    imageSrc,
    imageAlt,
    children,
  }: {
    title: string
    where: string
    when: string
    idea: string
    imageSrc?: string
    imageAlt?: string
    children: React.ReactNode
  }) => (
    <section className="rounded-2xl border bg-background overflow-hidden">
      <div className="px-4 py-3 bg-muted/30 border-b">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <h3 className="text-lg md:text-xl font-semibold text-foreground">{title}</h3>
          <div className="flex flex-wrap gap-2">
            <Pill kind="where" label="Where" value={where} />
            <Pill kind="when" label="When" value={when} />
            <Pill kind="idea" label="Key idea" value={idea} />
          </div>
        </div>
      </div>
      <div className="p-4">
        <div className="flex flex-col md:flex-row gap-5">
          {imageSrc && (
            <figure className="md:w-64 shrink-0">
              <div className="rounded-xl border overflow-hidden bg-muted/20">
                <img
                  src={imageSrc}
                  alt={imageAlt || title}
                  loading="lazy"
                  className="w-full h-auto aspect-[16/10] object-cover"
                />
              </div>
            </figure>
          )}
          <div className="min-w-0 text-muted-foreground space-y-3">{children}</div>
        </div>
      </div>
    </section>
  )

  return (
    <article className="text-sm md:text-base leading-7">
      <header className="space-y-3">
        <h2 className="text-xl md:text-2xl font-semibold text-foreground">History of Debate</h2>
        <p className="text-muted-foreground">
          Debate didn’t start as a school activity. Wherever communities built religion, law, and politics,
          they also built rules for argument—who speaks, what counts as proof, and how disagreement gets resolved.
        </p>
        <div className="rounded-xl border bg-muted/10 p-4">
          <p className="font-semibold text-foreground">Quick takeaway</p>
          <p className="mt-1 text-muted-foreground">
            Debate evolves when institutions change: courts and assemblies shape rhetoric, universities shape method,
            and tournaments shape speed, evidence norms, and strategy.
          </p>
        </div>
      </header>

      <div className="mt-6 grid grid-cols-1 lg:grid-cols-12 gap-6">
        <div className="lg:col-span-8 space-y-6">
          <Era
            title="Debate across civilizations (not just “the West”)"
            where="Many regions"
            when="Ancient → present"
            idea="Disputation as governance + philosophy"
          >
            <p>
              Long before debate became a school activity, societies around the world used formal argument to settle disputes, train leaders,
              and test ideas. You see structured disputation in political advising, legal reasoning, and religious/philosophical schools—often with
              explicit rules for who may speak, what counts as proof, and how decisions get made.
            </p>
            <p>
              One helpful way to think about “debate history” is not a single origin story, but a repeating pattern: whenever institutions need
              public justification (courts, councils, parliaments, universities), they invent or refine debate norms.
            </p>
          </Era>

          <Era
            title="Public speech and civic decision-making"
            where="Athens, Greece"
            when="5th–4th century BCE"
            idea="Persuasion becomes a civic skill"
            imageSrc={images.athensPnyx}
            imageAlt="On the Pnyx hill in Athens, associated with public speaking and civic assembly"
          >
            <p>
              In ancient Athens, democratic decision-making depended on public speech. Citizens argued in assemblies and courts,
              and <span className="font-semibold text-foreground">rhetoric</span>—the art of persuasion—became a core civic tool.
            </p>
            <p>
              The lasting legacy here isn’t just “speaking well,” but the idea that public decisions should be shaped by an
              open contest of reasons: competing claims, evidence, and judgment.
            </p>
            <div className="rounded-xl border bg-violet-600/10 p-4">
              <p className="font-semibold text-foreground">Callout: Why this matters</p>
              <p className="mt-1 text-muted-foreground">
                Many modern debate norms—burdens, refutation, and comparative evaluation—are echoes of civic argumentation:
                someone claims, someone challenges, and an audience decides.
              </p>
            </div>
          </Era>

          <Era
            title="Disputation as a method of learning"
            where="Medieval Europe"
            when="12th–15th centuries"
            idea="Debate becomes a formal academic method"
            imageSrc={images.disputation}
            imageAlt="Historical illustration associated with scholastic disputation"
          >
            <p>
              In religious and university settings, formal disputation became a structured way to teach and test doctrine.
              Medieval scholasticism emphasized logic, definitions, and careful responses.
            </p>
            <p>
              The classic pattern was: propose a question, present arguments, answer objections, and refine conclusions.
              This turns debate into disciplined inquiry—less “performance,” more “method.”
            </p>
            <div className="rounded-xl border bg-emerald-600/10 p-4">
              <p className="font-semibold text-foreground">Callout: A recognizable template</p>
              <p className="mt-1 text-muted-foreground">
                Many debate flows still look like scholastic disputation: claim → objection → response → weighing.
              </p>
            </div>
          </Era>

          <Era
            title="Catholic & religious disputation in the Reformation era"
            where="Worms, Leipzig, Trent (Europe)"
            when="1519–1563"
            idea="Authority, doctrine, and conscience argued in public"
            imageSrc={images.wormsLuther}
            imageAlt="Luther at the Diet of Worms"
          >
            <p>
              Across late medieval and early modern Europe, religious debate wasn’t just private theology—it shaped law, politics, and daily life.
              Councils, colloquies, and imperial assemblies became arenas where arguments about authority and doctrine were tested in front of power.
            </p>
            <div className="rounded-xl border bg-muted/10 p-4">
              <p className="font-semibold text-foreground">Key moments (a mini-timeline)</p>
              <ul className="mt-2 space-y-2 text-muted-foreground list-disc pl-5">
                <li>
                  <span className="font-semibold text-foreground">Leipzig Debate (1519)</span>: Luther and Johann Eck publicly argued issues like
                  papal authority and tradition—helping escalate the conflict into a wider European controversy.
                </li>
                <li>
                  <span className="font-semibold text-foreground">Diet of Worms (1521)</span>: Luther was asked to recant; he refused unless persuaded
                  by Scripture or reason. (The moment is often summarized by the phrase “Here I stand,” even though the exact wording is debated.)
                </li>
                <li>
                  <span className="font-semibold text-foreground">Regensburg Colloquy (1541)</span>: an attempt to reconcile Catholic and Protestant
                  theology through formal dialogue—showing both the promise and limits of “debate as compromise.”
                </li>
                <li>
                  <span className="font-semibold text-foreground">Council of Trent (1545–1563)</span>: the Catholic Church’s major doctrinal and reform
                  council of the Counter-Reformation, clarifying contested teachings and standardizing training and discipline.
                </li>
              </ul>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="rounded-xl border bg-sky-600/10 p-4">
                <div className="flex flex-wrap gap-2">
                  <Pill kind="when" label="When" value="16th century" />
                  <Pill kind="idea" label="Debate focus" value="Authority" />
                </div>
                <p className="mt-2 text-muted-foreground">
                  Who has the right to define doctrine—Scripture alone, Church tradition, councils, or a mix? Reformation-era debates made “rules of proof”
                  (texts, interpretations, precedent) a central battleground.
                </p>
              </div>
              <div className="rounded-xl border bg-emerald-600/10 p-4">
                <div className="flex flex-wrap gap-2">
                  <Pill kind="where" label="Where" value="Church + Empire" />
                  <Pill kind="idea" label="Debate focus" value="Institutions" />
                </div>
                <p className="mt-2 text-muted-foreground">
                  These weren’t just “two speakers and a judge”—they were debates embedded in institutions (empires, councils, courts) where the
                  consequences could be legal, political, and personal.
                </p>
              </div>
            </div>
          </Era>

          <Era
            title="The Lincoln–Douglas debates and public moral argument"
            where="Illinois, USA"
            when="1858 (Aug–Oct)"
            idea="Structured public debate shapes politics"
            imageSrc={images.lincolnDouglas}
            imageAlt="Illustration of the Lincoln–Douglas debates"
          >
            <p>
              The Lincoln–Douglas debates between Abraham Lincoln and Stephen Douglas showed how structured argument before an audience
              could shape national consciousness. The topic—slavery and the future of the United States—made moral reasoning and political principle
              unavoidable.
            </p>
            <p>
              Later, competitive debate borrowed the name “Lincoln–Douglas” to describe a one-on-one format that emphasizes values, philosophy,
              and ethical reasoning.
            </p>
            <div className="rounded-xl border bg-sky-600/10 p-4">
              <p className="font-semibold text-foreground">Where/When detail</p>
              <p className="mt-1 text-muted-foreground">
                The debates were held across seven Illinois towns during the 1858 Senate campaign—an early example of debate as mass public spectacle.
              </p>
            </div>
          </Era>

          <Era
            title="Modern competitive debate (formats, leagues, and strategy)"
            where="Global"
            when="20th–21st centuries"
            idea="Debate becomes an intellectual sport"
            imageSrc={images.modernDebate}
            imageAlt="Modern debate event in a formal setting"
          >
            <p>
              During the twentieth century, debate evolved into formal competition with leagues, tournaments, and specialized formats
              (LD, Policy, Parliamentary, Public Forum, World Schools). With that came tighter time limits, clearer roles, and more standardized
              expectations for evidence and organization.
            </p>
            <p>
              Over time, strategy became more technical: line-by-line refutation, impact comparison, and specialized vocabulary for how arguments interact.
              At the same time, different circuits developed different styles—traditional persuasion, technical flow debate, and critical approaches.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="rounded-xl border bg-sky-600/10 p-4">
                <div className="flex flex-wrap gap-2">
                  <Pill kind="idea" label="Shift" value="Speed & technical density" />
                </div>
                <p className="mt-2 text-muted-foreground">
                  As evidence access expanded and flowing improved, some circuits emphasized faster delivery and deeper technical interaction.
                  This is where terms like “line-by-line,” “extensions,” and “impact calculus” become central.
                </p>
              </div>
              <div className="rounded-xl border bg-violet-600/10 p-4">
                <div className="flex flex-wrap gap-2">
                  <Pill kind="idea" label="Shift" value="Critical/identity argumentation" />
                </div>
                <p className="mt-2 text-muted-foreground">
                  Other circuits expanded what counts as “argument,” including critiques of assumptions, language, power, and identity.
                  Narrative and performance became more common in some spaces, and judges debated norms like fairness and accessibility.
                </p>
              </div>
            </div>
            <p>
              Finally, online debate accelerated in the 2020s: practices, coaching, and tournaments moved onto digital platforms.
              That shift made recording and review easier, expanded access across geography, and changed how communities organize.
            </p>
            <div className="rounded-xl border bg-emerald-600/10 p-4">
              <p className="font-semibold text-foreground">Callout: Debate today</p>
              <p className="mt-1 text-muted-foreground">
                Modern debate is a multi-paradigm ecosystem: fast and technical in some rooms, values-focused in others, and increasingly global and online.
              </p>
            </div>
          </Era>

          <section className="rounded-2xl border bg-background p-4">
            <h3 className="text-lg font-semibold text-foreground">What stays constant</h3>
            <p className="mt-2 text-muted-foreground">
              Across centuries, debate changes its venues and techniques, but the core purpose stays the same:
              a structured contest of ideas where claims are tested, reasons are compared, and judgments are rendered through disciplined argument.
            </p>
          </section>
        </div>

        <aside className="lg:col-span-4 space-y-4">
          <div className="rounded-2xl border bg-background p-4">
            <h3 className="text-base font-semibold text-foreground">Timeline (at a glance)</h3>
            <div className="mt-3 space-y-3 text-muted-foreground">
              <div className="rounded-lg border bg-muted/10 p-3">
                <p className="font-semibold text-foreground">Athens civic rhetoric</p>
                <div className="mt-2 flex flex-wrap gap-2">
                  <Pill kind="where" label="Where" value="Athens" />
                  <Pill kind="when" label="When" value="5th–4th c. BCE" />
                </div>
              </div>
              <div className="rounded-lg border bg-muted/10 p-3">
                <p className="font-semibold text-foreground">Scholastic disputation</p>
                <div className="mt-2 flex flex-wrap gap-2">
                  <Pill kind="where" label="Where" value="Universities / Church schools" />
                  <Pill kind="when" label="When" value="12th–15th c." />
                </div>
              </div>
              <div className="rounded-lg border bg-muted/10 p-3">
                <p className="font-semibold text-foreground">Reformation disputations</p>
                <div className="mt-2 flex flex-wrap gap-2">
                  <Pill kind="where" label="Where" value="Worms / Trent" />
                  <Pill kind="when" label="When" value="1519–1563" />
                </div>
              </div>
              <div className="rounded-lg border bg-muted/10 p-3">
                <p className="font-semibold text-foreground">Lincoln–Douglas debates</p>
                <div className="mt-2 flex flex-wrap gap-2">
                  <Pill kind="where" label="Where" value="Illinois" />
                  <Pill kind="when" label="When" value="1858" />
                </div>
              </div>
              <div className="rounded-lg border bg-muted/10 p-3">
                <p className="font-semibold text-foreground">Competitive formats</p>
                <div className="mt-2 flex flex-wrap gap-2">
                  <Pill kind="where" label="Where" value="Global" />
                  <Pill kind="when" label="When" value="20th–21st c." />
                </div>
              </div>
            </div>
          </div>

          <div className="rounded-2xl border bg-background p-4">
            <h3 className="text-base font-semibold text-foreground">Key themes</h3>
            <div className="mt-3 flex flex-wrap gap-2">
              <Pill kind="idea" label="Theme" value="Rules for speech" />
              <Pill kind="idea" label="Theme" value="Proof & evidence" />
              <Pill kind="idea" label="Theme" value="Institutions" />
              <Pill kind="idea" label="Theme" value="Method & training" />
              <Pill kind="idea" label="Theme" value="Competition" />
            </div>
          </div>
        </aside>
      </div>
    </article>
  )
}

function ModernDebateArticle() {
  const images = {
    // Public link (Wikimedia Commons)
    tournament: 'https://upload.wikimedia.org/wikipedia/commons/d/d8/Debatetournament.jpg',
  }

  return (
    <article className="text-sm md:text-base leading-7">
      <h3 className="text-lg md:text-xl font-semibold text-foreground">
        An Overview of Modern Competitive Debate
      </h3>

      <p className="mt-3 text-muted-foreground">
        Debate today is a global competitive activity with formal rules, leagues, seasons, and
        ranking systems, much like organized sports. It spans middle school, high school, college,
        and international circuits, and it exists both as an academic discipline and a
        professionalized competitive activity.
      </p>

      <p className="mt-3 text-muted-foreground">
        At its core, debate is the structured comparison of arguments under a shared set of rules,
        judged by trained adjudicators, with the goal of determining which side best supports a
        resolution or motion.
      </p>

      <section className="mt-6 after:content-[''] after:block after:clear-both">
        <figure className="w-full sm:w-72 sm:float-right sm:ml-6 sm:mb-4 border rounded-lg overflow-hidden bg-muted/30">
          <img
            src={images.tournament}
            alt="Students competing in a debate tournament"
            loading="lazy"
            className="w-full h-auto"
          />
          <figcaption className="px-3 py-2 text-xs text-muted-foreground">
            Modern debate is organized into formats, leagues, tournaments, and seasons—like an
            intellectual sport.
          </figcaption>
        </figure>

        <h4 className="text-lg font-semibold text-foreground">Major Debate Formats</h4>
        <p className="mt-2 text-muted-foreground">
          In the United States, the most common competitive formats are:
        </p>

        <div className="mt-3 space-y-4 text-muted-foreground">
          <div>
            <p className="font-semibold text-foreground">Lincoln-Douglas (LD)</p>
            <p>
              A one-on-one format focused on values and philosophy. Debaters argue moral and
              political questions, often through ethical frameworks like utilitarianism, deontology,
              or social contract theory. It emphasizes logical consistency, moral reasoning, and
              clash. This is one of the most popular high-school formats.
            </p>
          </div>

          <div>
            <p className="font-semibold text-foreground">Public Forum (PF)</p>
            <p>
              A two-on-two format designed to be accessible to a general audience. Topics are
              current events, and rounds emphasize persuasion, clarity, and impact weighing rather
              than dense technical theory. PF is now the fastest-growing high school format in the
              U.S.
            </p>
          </div>

          <div>
            <p className="font-semibold text-foreground">Policy Debate (CX)</p>
            <p>
              A two-on-two format centered on policy proposals. One team affirms a yearly
              resolution; the other negates it. This is the most technical and research-heavy
              format, involving complex evidence comparison, counterplans, disadvantages, kritiks,
              and theory. It dominates college debate and remains influential in high school.
            </p>
          </div>

          <div>
            <p className="font-semibold text-foreground">Parliamentary Debate</p>
            <p>
              Modeled on British parliamentary procedure, this format uses motions announced
              shortly before the round, emphasizing improvisation, rhetoric, and logical structure.
              Popular in college and internationally.
            </p>
          </div>

          <div>
            <p className="font-semibold text-foreground">World Schools Debate (WSD)</p>
            <p>
              An international format combining prepared and impromptu motions, used in world
              championships and national teams.
            </p>
          </div>
        </div>
      </section>

      <section className="mt-8">
        <h4 className="text-lg font-semibold text-foreground">Debate Styles</h4>
        <p className="mt-2 text-muted-foreground">Modern debate includes several stylistic approaches:</p>
        <ul className="mt-3 list-disc pl-5 space-y-2 text-muted-foreground">
          <li>
            <span className="font-semibold text-foreground">Traditional persuasion</span>: Clear
            speaking, judge-friendly, rhetorical.
          </li>
          <li>
            <span className="font-semibold text-foreground">Flow / technical debate</span>: Rapid
            delivery, line-by-line refutation, strict rule interpretation.
          </li>
          <li>
            <span className="font-semibold text-foreground">Critical / K-debate</span>: Focus on
            philosophy, power, identity, language, and structural critique.
          </li>
          <li>
            <span className="font-semibold text-foreground">Progressive theory debate</span>:
            Meta-argumentation about fairness, education, and procedural rules.
          </li>
        </ul>
      </section>

      <section className="mt-8">
        <h4 className="text-lg font-semibold text-foreground">Major Organizations</h4>
        <p className="mt-2 text-muted-foreground">
          The main governing and organizing bodies in the U.S. include:
        </p>
        <ul className="mt-3 list-disc pl-5 space-y-2 text-muted-foreground">
          <li>
            <span className="font-semibold text-foreground">NSDA (National Speech &amp; Debate Association)</span>:
            The largest high-school league in the United States. It sets event rules, manages
            rankings, and hosts the National Tournament each summer.
          </li>
          <li>
            <span className="font-semibold text-foreground">NCFL (National Catholic Forensic League)</span>:
            Runs a parallel national circuit and championship tournament.
          </li>
          <li>
            <span className="font-semibold text-foreground">CEDA (Cross Examination Debate Association)</span>:
            One of the two primary college policy debate organizations.
          </li>
          <li>
            <span className="font-semibold text-foreground">NDT (National Debate Tournament)</span>: The most
            prestigious college policy championship.
          </li>
          <li>
            <span className="font-semibold text-foreground">NPDA (National Parliamentary Debate Association)</span>:
            Governs college parliamentary debate.
          </li>
          <li>
            <span className="font-semibold text-foreground">WSDC (World Schools Debating Championships)</span>:
            The premier international secondary-school championship.
          </li>
        </ul>
      </section>

      <section className="mt-8">
        <h4 className="text-lg font-semibold text-foreground">How the Season Works</h4>
        <p className="mt-2 text-muted-foreground">A typical competitive season follows this structure:</p>
        <ol className="mt-3 list-decimal pl-5 space-y-2 text-muted-foreground">
          <li>
            <span className="font-semibold text-foreground">Annual Topic Release</span>: Each year,
            resolutions are released for LD, PF, and Policy by national committees.
          </li>
          <li>
            <span className="font-semibold text-foreground">Invitational Tournaments</span>: Schools
            host weekend tournaments where teams earn experience, rankings, and qualifications.
          </li>
          <li>
            <span className="font-semibold text-foreground">District / Regional Qualifiers</span>:
            Students compete to qualify for national championships.
          </li>
          <li>
            <span className="font-semibold text-foreground">National Circuit (TOC, NSDA, NDT, etc.)</span>:
            Elite tournaments such as the Tournament of Champions (TOC) and National Debate
            Tournament define the highest competitive level.
          </li>
          <li>
            <span className="font-semibold text-foreground">National Championships</span>: Culminating
            events where the best teams and speakers compete.
          </li>
        </ol>
      </section>

      <section className="mt-8">
        <h4 className="text-lg font-semibold text-foreground">Judging and Ballots</h4>
        <p className="mt-2 text-muted-foreground">
          Debate is judged by former competitors, coaches, trained community judges, and in some
          formats, lay judges.
        </p>
        <p className="mt-2 text-muted-foreground">Judges evaluate:</p>
        <ul className="mt-3 list-disc pl-5 space-y-2 text-muted-foreground">
          <li>Argument quality</li>
          <li>Evidence</li>
          <li>Logical coherence</li>
          <li>Impact calculus</li>
          <li>Framework application</li>
          <li>Technical execution</li>
          <li>Persuasion</li>
        </ul>
      </section>

      <section className="mt-8">
        <h4 className="text-lg font-semibold text-foreground">Modern Evolution</h4>
        <p className="mt-2 text-muted-foreground">
          Today, debate exists both in-person, at schools and universities, and online, using video
          platforms and specialized debate software. With AI tools, debaters can practice opponents,
          accelerate research, and get judging assistance and feedback.
        </p>
        <p className="mt-2 text-muted-foreground">
          The activity has become faster, more technical, more philosophically diverse, and more
          accessible globally—yet its core purpose remains unchanged: to train students in critical
          thinking, ethical reasoning, public speaking, research, strategic decision-making, and
          democratic discourse.
        </p>
        <p className="mt-2 text-muted-foreground">
          Modern competitive debate is no longer just an extracurricular. It is a structured
          intellectual sport with international leagues, formal rulesets, professional coaching,
          data analytics, and now, artificial intelligence—entering a new era of how argumentation
          is practiced, taught, and evaluated.
        </p>
      </section>
    </article>
  )
}

function LDFrameworkDiagram() {
  // Inline SVG diagram (no external dependencies). Designed to be responsive.
  return (
    <svg
      viewBox="0 0 960 360"
      role="img"
      aria-label="Diagram: how an LD round flows from Value and Criterion to Contentions, Weighing, and the Ballot"
      className="w-full h-auto"
    >
      <defs>
        <linearGradient id="ldGrad" x1="0" x2="1" y1="0" y2="0">
          <stop offset="0%" stopColor="#7c3aed" stopOpacity="0.15" />
          <stop offset="100%" stopColor="#14b8a6" stopOpacity="0.12" />
        </linearGradient>
      </defs>

      {/* Background */}
      <rect x="0" y="0" width="960" height="360" rx="16" fill="url(#ldGrad)" />

      {/* Boxes */}
      <g fontFamily="ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial">
        <g>
          <rect x="40" y="60" width="210" height="90" rx="14" fill="#111827" opacity="0.9" />
          <text x="145" y="98" textAnchor="middle" fill="#fff" fontSize="18" fontWeight="700">
            Value
          </text>
          <text x="145" y="124" textAnchor="middle" fill="#cbd5e1" fontSize="14">
            the moral goal
          </text>
        </g>

        <g>
          <rect x="280" y="60" width="260" height="90" rx="14" fill="#111827" opacity="0.9" />
          <text x="410" y="98" textAnchor="middle" fill="#fff" fontSize="18" fontWeight="700">
            Criterion / Standard
          </text>
          <text x="410" y="124" textAnchor="middle" fill="#cbd5e1" fontSize="14">
            the metric to evaluate impacts
          </text>
        </g>

        <g>
          <rect x="570" y="60" width="350" height="90" rx="14" fill="#111827" opacity="0.9" />
          <text x="745" y="98" textAnchor="middle" fill="#fff" fontSize="18" fontWeight="700">
            Contentions (Offense)
          </text>
          <text x="745" y="124" textAnchor="middle" fill="#cbd5e1" fontSize="14">
            warrants → impacts
          </text>
        </g>

        <g>
          <rect x="160" y="210" width="260" height="90" rx="14" fill="#0f172a" opacity="0.92" />
          <text x="290" y="248" textAnchor="middle" fill="#fff" fontSize="18" fontWeight="700">
            Weighing
          </text>
          <text x="290" y="274" textAnchor="middle" fill="#cbd5e1" fontSize="14">
            magnitude · probability · timeframe
          </text>
        </g>

        <g>
          <rect x="470" y="210" width="280" height="90" rx="14" fill="#0f172a" opacity="0.92" />
          <text x="610" y="248" textAnchor="middle" fill="#fff" fontSize="18" fontWeight="700">
            Collapse
          </text>
          <text x="610" y="274" textAnchor="middle" fill="#cbd5e1" fontSize="14">
            pick 1–2 winning issues
          </text>
        </g>

        <g>
          <rect x="790" y="210" width="130" height="90" rx="14" fill="#065f46" opacity="0.92" />
          <text x="855" y="248" textAnchor="middle" fill="#fff" fontSize="18" fontWeight="700">
            Ballot
          </text>
          <text x="855" y="274" textAnchor="middle" fill="#d1fae5" fontSize="14">
            winner + RFD
          </text>
        </g>

        {/* Arrows */}
        <g stroke="#334155" strokeWidth="6" strokeLinecap="round" fill="none" opacity="0.9">
          <path d="M250 105 L280 105" />
          <path d="M540 105 L570 105" />
          <path d="M410 150 C410 190 330 190 290 210" />
          <path d="M745 150 C745 190 650 190 610 210" />
          <path d="M750 255 L790 255" />
        </g>
        <g fill="#334155" opacity="0.9">
          <polygon points="278,105 260,96 260,114" />
          <polygon points="568,105 550,96 550,114" />
          <polygon points="290,210 276,196 298,198" />
          <polygon points="610,210 596,196 618,198" />
          <polygon points="788,255 770,246 770,264" />
        </g>
      </g>
    </svg>
  )
}

function LincolnDouglasArticle() {
  const images = {
    // Public link (Wikimedia Commons)
    lincolnDouglas:
      'https://upload.wikimedia.org/wikipedia/commons/thumb/a/a9/The_Lincoln-Douglas_debates_of_1858_%281908%29_%2814586661868%29.jpg/1280px-The_Lincoln-Douglas_debates_of_1858_%281908%29_%2814586661868%29.jpg',
  }

  return (
    <article className="text-sm md:text-base leading-7">
      <header className="space-y-2">
        <h3 className="text-xl md:text-2xl font-semibold text-foreground">
          Lincoln–Douglas Debate (LD): A Student-Friendly Overview
        </h3>
        <p className="text-muted-foreground">
          Lincoln–Douglas (LD) is a one-on-one debate event common in U.S. high schools. The goal is
          to compare arguments under shared rules and decide which side best supports the
          resolution—usually through a clear moral <GlossaryTerm term="Framework">framework</GlossaryTerm> and careful{' '}
          <GlossaryTerm term="Weighing">weighing</GlossaryTerm>.
        </p>
      </header>

      <div className="mt-6 grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Main column */}
        <div className="lg:col-span-8 space-y-8">
          <section className="space-y-3">
            <h4 className="text-lg font-semibold text-foreground">Start here: key terms (glossary)</h4>
            <p className="text-muted-foreground">
              Before structure and strategy, learn the vocabulary you’ll hear in round.
            </p>

            <div className="overflow-x-auto rounded-lg border">
              <table className="w-full text-sm">
                <thead className="bg-muted/40">
                  <tr className="text-left">
                    <th className="px-3 py-2 font-semibold text-foreground">Term</th>
                    <th className="px-3 py-2 font-semibold text-foreground">What it means</th>
                  </tr>
                </thead>
                <tbody className="divide-y text-muted-foreground">
                  <tr>
                    <td className="px-3 py-2 font-semibold text-foreground">
                      <GlossaryTerm term="Aff">Affirmative (Aff)</GlossaryTerm>
                    </td>
                    <td className="px-3 py-2">Argues the resolution should be affirmed.</td>
                  </tr>
                  <tr>
                    <td className="px-3 py-2 font-semibold text-foreground">
                      <GlossaryTerm term="Neg">Negative (Neg)</GlossaryTerm>
                    </td>
                    <td className="px-3 py-2">Argues the resolution should not be affirmed.</td>
                  </tr>
                  <tr>
                    <td className="px-3 py-2 font-semibold text-foreground">Case</td>
                    <td className="px-3 py-2">Your prepared advocacy: framework + contentions.</td>
                  </tr>
                  <tr>
                    <td className="px-3 py-2 font-semibold text-foreground">
                      <GlossaryTerm term="Framework">Framework</GlossaryTerm>
                    </td>
                    <td className="px-3 py-2">
                      The lens for deciding the round (often a value + criterion/standard).
                    </td>
                  </tr>
                  <tr>
                    <td className="px-3 py-2 font-semibold text-foreground">
                      <GlossaryTerm term="Value">Value</GlossaryTerm>
                    </td>
                    <td className="px-3 py-2">The moral goal (e.g., justice, liberty, welfare).</td>
                  </tr>
                  <tr>
                    <td className="px-3 py-2 font-semibold text-foreground">
                      <GlossaryTerm term="Criterion">Criterion</GlossaryTerm> /{' '}
                      <GlossaryTerm term="Standard">Standard</GlossaryTerm>
                    </td>
                    <td className="px-3 py-2">
                      The test for reaching the value (how the judge should evaluate impacts).
                    </td>
                  </tr>
                  <tr>
                    <td className="px-3 py-2 font-semibold text-foreground">
                      <GlossaryTerm term="Contention">Contention</GlossaryTerm>
                    </td>
                    <td className="px-3 py-2">A main argument with a claim, warrants, and impacts.</td>
                  </tr>
                  <tr>
                    <td className="px-3 py-2 font-semibold text-foreground">
                      <GlossaryTerm term="Warrant">Warrant</GlossaryTerm>
                    </td>
                    <td className="px-3 py-2">The reasoning/evidence that supports a claim.</td>
                  </tr>
                  <tr>
                    <td className="px-3 py-2 font-semibold text-foreground">
                      <GlossaryTerm term="Impact">Impact</GlossaryTerm>
                    </td>
                    <td className="px-3 py-2">Why the argument matters (the consequence).</td>
                  </tr>
                  <tr>
                    <td className="px-3 py-2 font-semibold text-foreground">
                      <GlossaryTerm term="Weighing">Weighing</GlossaryTerm>
                    </td>
                    <td className="px-3 py-2">
                      Comparing impacts (magnitude, probability, timeframe, moral priority).
                    </td>
                  </tr>
                  <tr>
                    <td className="px-3 py-2 font-semibold text-foreground">
                      <GlossaryTerm term="Offense">Offense</GlossaryTerm>
                    </td>
                    <td className="px-3 py-2">Reasons you win (your “vote for me” arguments).</td>
                  </tr>
                  <tr>
                    <td className="px-3 py-2 font-semibold text-foreground">
                      <GlossaryTerm term="Defense">Defense</GlossaryTerm>
                    </td>
                    <td className="px-3 py-2">Reasons the opponent’s offense doesn’t apply or matter.</td>
                  </tr>
                  <tr>
                    <td className="px-3 py-2 font-semibold text-foreground">
                      <GlossaryTerm term="Flow">Flow</GlossaryTerm> / <GlossaryTerm term="Flowing">Flowing</GlossaryTerm>
                    </td>
                    <td className="px-3 py-2">
                      Structured note-taking to track arguments and responses over speeches.
                    </td>
                  </tr>
                  <tr>
                    <td className="px-3 py-2 font-semibold text-foreground">
                      <GlossaryTerm term="Extension">Extension</GlossaryTerm>
                    </td>
                    <td className="px-3 py-2">
                      Carrying an argument forward with explanation + impact story.
                    </td>
                  </tr>
                  <tr>
                    <td className="px-3 py-2 font-semibold text-foreground">
                      <GlossaryTerm term="Drop">Drop</GlossaryTerm>
                    </td>
                    <td className="px-3 py-2">An argument left unanswered; often treated as conceded.</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>

          <section className="space-y-3">
            <h4 className="text-lg font-semibold text-foreground">What LD is (in plain language)</h4>
            <div className="rounded-lg border bg-muted/20 p-4 text-muted-foreground">
              <p>
                LD topics are usually about what is <span className="font-semibold text-foreground">morally</span>{' '}
                justified or what principle should guide society. You’re not required to propose a
                detailed plan like Policy debate.
              </p>
              <p className="mt-2">
                <span className="font-semibold text-foreground">Example resolution:</span> “Resolved:
                Civil disobedience is morally justified in a democracy.”
              </p>
              <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="rounded-md border bg-background p-3">
                  <p className="font-semibold text-foreground">Affirmative</p>
                  <p>Gives reasons the resolution should be affirmed.</p>
                </div>
                <div className="rounded-md border bg-background p-3">
                  <p className="font-semibold text-foreground">Negative</p>
                  <p>Gives reasons the resolution should not be affirmed.</p>
                </div>
              </div>
              <p className="mt-3">
                <span className="font-semibold text-foreground">What wins the ballot:</span> the side
                that best defends its framework and wins the most important offense under that lens.
              </p>
            </div>
          </section>

          <section className="space-y-3">
            <h4 className="text-lg font-semibold text-foreground">How LD is structured (framework)</h4>
            <p className="text-muted-foreground">
              Many LD rounds are decided by a simple question:{' '}
              <span className="font-semibold text-foreground">
                under the best moral lens, whose impacts matter more?
              </span>
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div className="rounded-lg border bg-background p-4">
                <p className="font-semibold text-foreground">
                  <GlossaryTerm term="Value">Value</GlossaryTerm>
                </p>
                <p className="text-muted-foreground">
                  The ultimate moral goal (e.g., Justice, Liberty, Social Welfare).
                </p>
              </div>
              <div className="rounded-lg border bg-background p-4">
                <p className="font-semibold text-foreground">
                  <GlossaryTerm term="Criterion">Criterion</GlossaryTerm> /{' '}
                  <GlossaryTerm term="Standard">Standard</GlossaryTerm>
                </p>
                <p className="text-muted-foreground">
                  The metric used to evaluate impacts (e.g., maximizing well-being, protecting rights).
                </p>
              </div>
              <div className="rounded-lg border bg-background p-4">
                <p className="font-semibold text-foreground">
                  <GlossaryTerm term="Contention">Contentions</GlossaryTerm>
                </p>
                <p className="text-muted-foreground">
                  Your main arguments that connect warrants → impacts under the criterion.
                </p>
              </div>
            </div>

            <div className="rounded-lg border bg-muted/20 p-4">
              <p className="font-semibold text-foreground">Rule of thumb</p>
              <p className="text-muted-foreground">
                Framework tells the judge how to evaluate the round. Offense + weighing tells the
                judge why your side should win under that evaluation.
              </p>
            </div>
          </section>

          <section className="space-y-3">
            <h4 className="text-lg font-semibold text-foreground">Round structure: speeches & timing</h4>
            <p className="text-muted-foreground">
              Exact times can vary by league, but the overall structure is consistent:
            </p>

            <div className="overflow-x-auto rounded-lg border">
              <table className="w-full text-sm">
                <thead className="bg-muted/40">
                  <tr className="text-left">
                    <th className="px-3 py-2 font-semibold text-foreground">Speech</th>
                    <th className="px-3 py-2 font-semibold text-foreground">Time</th>
                    <th className="px-3 py-2 font-semibold text-foreground">Purpose</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  <tr>
                    <td className="px-3 py-2">1AC (Aff)</td>
                    <td className="px-3 py-2">6 min</td>
                    <td className="px-3 py-2 text-muted-foreground">Present the full affirmative case</td>
                  </tr>
                  <tr>
                    <td className="px-3 py-2">Cross-Ex (Neg asks Aff)</td>
                    <td className="px-3 py-2">3 min</td>
                    <td className="px-3 py-2 text-muted-foreground">Clarify, expose weaknesses, lock concessions</td>
                  </tr>
                  <tr>
                    <td className="px-3 py-2">1NC (Neg)</td>
                    <td className="px-3 py-2">7 min</td>
                    <td className="px-3 py-2 text-muted-foreground">Neg case + refutation; set up pressure</td>
                  </tr>
                  <tr>
                    <td className="px-3 py-2">Cross-Ex (Aff asks Neg)</td>
                    <td className="px-3 py-2">3 min</td>
                    <td className="px-3 py-2 text-muted-foreground">Clarify positions; trap contradictions</td>
                  </tr>
                  <tr>
                    <td className="px-3 py-2">1AR (Aff)</td>
                    <td className="px-3 py-2">4 min</td>
                    <td className="px-3 py-2 text-muted-foreground">Answer everything; extend offense; weigh</td>
                  </tr>
                  <tr>
                    <td className="px-3 py-2">2NR (Neg)</td>
                    <td className="px-3 py-2">6 min</td>
                    <td className="px-3 py-2 text-muted-foreground">Collapse to 1–2 winning issues; impact calculus</td>
                  </tr>
                  <tr>
                    <td className="px-3 py-2">2AR (Aff)</td>
                    <td className="px-3 py-2">3 min</td>
                    <td className="px-3 py-2 text-muted-foreground">Final crystallization; “write the decision”</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>

          <section className="space-y-3">
            <h4 className="text-lg font-semibold text-foreground">What happens in each part of the round</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-muted-foreground">
              <div className="rounded-lg border bg-background p-4">
                <p className="font-semibold text-foreground">Constructives (1AC / 1NC)</p>
                <p>
                  You introduce your main positions: framework, contentions, and key evidence. You
                  want clear claims, strong warrants, and impacts that matter under your standard.
                </p>
              </div>
              <div className="rounded-lg border bg-background p-4">
                <p className="font-semibold text-foreground">Cross-examinations</p>
                <p>
                  A question period. Use it to clarify, expose assumptions, and set up later
                  refutation (“So you agree that…?”).
                </p>
              </div>
              <div className="rounded-lg border bg-background p-4">
                <p className="font-semibold text-foreground">Rebuttals (1AR / 2NR / 2AR)</p>
                <p>
                  You respond, extend, and weigh. As the round progresses, you should narrow (“collapse”)
                  to the few arguments that actually decide the ballot.
                </p>
              </div>
              <div className="rounded-lg border bg-background p-4">
                <p className="font-semibold text-foreground">Weighing & crystallization</p>
                <p>
                  The last speeches should explain why your impacts come first—by probability,
                  magnitude, timeframe, or moral priority under the framework.
                </p>
              </div>
            </div>
          </section>

          <section className="space-y-3">
            <h4 className="text-lg font-semibold text-foreground">Evidence: how it works in LD</h4>
            <div className="rounded-lg border bg-muted/20 p-4 text-muted-foreground">
              <p>
                Evidence in LD is typically read as <span className="font-semibold text-foreground">cards</span>:
                short excerpts from credible sources with a citation. Evidence supports warrants, but
                it doesn’t replace explanation—judges still need to understand what your evidence
                proves and why it matters.
              </p>
              <ul className="mt-3 list-disc pl-5 space-y-2">
                <li>
                  <span className="font-semibold text-foreground">Citations</span>: Author, credentials (if relevant),
                  source, date, and where it was published.
                </li>
                <li>
                  <span className="font-semibold text-foreground">Quality</span>: Prefer primary sources, reputable
                  journals, major news outlets, and qualified experts.
                </li>
                <li>
                  <span className="font-semibold text-foreground">Cutting</span>: You can shorten a card, but don’t
                  change the meaning. Misquoting can lose rounds.
                </li>
                <li>
                  <span className="font-semibold text-foreground">Calling for evidence</span>: Judges or opponents may
                  ask to see a card after it’s read. Be organized so you can produce it quickly.
                </li>
              </ul>
              <p className="mt-3">
                Note: rules on paraphrasing and evidence practices can vary by league/tournament—follow
                your circuit’s norms and your coach’s guidance.
              </p>
            </div>
          </section>

          <section className="space-y-3">
            <h4 className="text-lg font-semibold text-foreground">Ballots: how decisions are made</h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="rounded-lg border bg-background p-4">
                <p className="font-semibold text-foreground">Ballot (winner + RFD)</p>
                <p className="text-muted-foreground">
                  The judge writes an RFD (Reason for Decision). Most RFDs answer:
                  which framework mattered, which offense survived, and how weighing decided the round.
                </p>
              </div>
              <div className="rounded-lg border bg-background p-4">
                <p className="font-semibold text-foreground">Speaker points (often 26–30)</p>
                <p className="text-muted-foreground">
                  Clarity, strategy, organization, quality of argumentation, cross-ex performance,
                  and professionalism.
                </p>
              </div>
            </div>
            <div className="rounded-lg border bg-muted/20 p-4 text-muted-foreground">
              <p className="font-semibold text-foreground">A simple way to think about judging</p>
              <p className="mt-1">
                Judges usually decide in this order:
              </p>
              <ol className="mt-2 list-decimal pl-5 space-y-1">
                <li>What framework should we use?</li>
                <li>Under that framework, which side has winning offense?</li>
                <li>When we weigh impacts, which side comes out ahead?</li>
              </ol>
            </div>
          </section>

          <section className="space-y-3">
            <h4 className="text-lg font-semibold text-foreground">Flow notes: how to keep track of the debate</h4>
            <div className="rounded-lg border bg-muted/20 p-4 text-muted-foreground">
              <p>
                <span className="font-semibold text-foreground">Flowing</span> is structured note-taking.
                You track each contention and the responses to it in columns (speech by speech).
                The judge “flows” too—so a clear flow often equals a clear ballot.
              </p>
              <ul className="mt-3 list-disc pl-5 space-y-2">
                <li>
                  Write <span className="font-semibold text-foreground">short tags</span> (2–6 words) instead of full
                  sentences.
                </li>
                <li>
                  Keep responses <span className="font-semibold text-foreground">directly under</span> the argument they answer.
                </li>
                <li>
                  Mark drops and key concessions clearly (stars, highlights, arrows).
                </li>
                <li>
                  In rebuttals, extend arguments by referencing the flow and explaining why they still win.
                </li>
              </ul>
              <p className="mt-3">
                Your goal isn’t to write everything—it’s to capture the structure of the clash so you can
                compare arguments and weigh impacts later.
              </p>
            </div>
          </section>
        </div>

        {/* Sidebar */}
        <aside className="lg:col-span-4 space-y-6">
          <section className="rounded-lg border bg-background p-4">
            <h4 className="text-base font-semibold text-foreground">At-a-glance</h4>
            <ul className="mt-3 list-disc pl-5 space-y-2 text-muted-foreground">
              <li>
                LD compares moral arguments under a framework and decides which side’s offense matters more.
              </li>
              <li>Prep time is limited (often ~4–5 minutes total).</li>
              <li>Framework + weighing often decides close rounds.</li>
              <li>Strong strategy = clear collapse + clean extensions.</li>
            </ul>
          </section>

          <section className="rounded-lg border bg-background p-4">
            <h4 className="text-base font-semibold text-foreground">Framework diagram</h4>
            <p className="mt-2 text-sm text-muted-foreground">
              A simple mental model for how judges move from framework to ballot.
            </p>
            <div className="mt-3 overflow-hidden rounded-lg border bg-muted/20 p-2">
              <LDFrameworkDiagram />
            </div>
          </section>

          <section className="rounded-lg border bg-background p-4">
            <h4 className="text-base font-semibold text-foreground">Types of judges</h4>
            <div className="mt-3 space-y-3 text-muted-foreground">
              <div>
                <p className="font-semibold text-foreground">Traditional / Lay</p>
                <p>Clarity and persuasion matter; less appetite for dense theory.</p>
              </div>
              <div>
                <p className="font-semibold text-foreground">Flow / Technical</p>
                <p>Line-by-line; dropped arguments matter; theory/philosophy welcomed.</p>
              </div>
              <div>
                <p className="font-semibold text-foreground">Progressive / National Circuit</p>
                <p>Kritiks/theory and technical precision; fast delivery is common.</p>
              </div>
            </div>
          </section>

          <section className="rounded-lg border bg-background p-4">
            <h4 className="text-base font-semibold text-foreground">Philosophy toolkit</h4>
            <p className="mt-2 text-sm text-muted-foreground">
              Common lenses you’ll hear in LD:
            </p>
            <div className="mt-3 flex flex-wrap gap-2">
              {[
                'Kant / deontology',
                'Utilitarianism',
                'Rawls',
                'Natural law',
                'Social contract',
                'Virtue ethics',
                'Rights theory',
              ].map((t) => (
                <span key={t} className="text-xs px-2 py-1 rounded-full border bg-muted/30">
                  {t}
                </span>
              ))}
            </div>
          </section>

          <figure className="rounded-lg border overflow-hidden bg-muted/20">
            <img
              src={images.lincolnDouglas}
              alt="Historical image associated with the Lincoln–Douglas debates"
              loading="lazy"
              className="w-full h-auto"
            />
            <figcaption className="px-3 py-2 text-xs text-muted-foreground">
              The format’s name comes from the famous 1858 Lincoln–Douglas debates—modern LD adapts
              the idea of public moral argument into a competitive structure.
            </figcaption>
          </figure>
        </aside>
      </div>
    </article>
  )
}

function LDElementsArticle() {
  const Badge = ({
    kind,
    children,
  }: {
    kind: 'required' | 'optional' | 'conditional'
    children: React.ReactNode
  }) => {
    const styles =
      kind === 'required'
        ? 'bg-emerald-600/15 text-emerald-700 dark:text-emerald-300 border-emerald-600/25'
        : kind === 'optional'
          ? 'bg-sky-600/15 text-sky-700 dark:text-sky-300 border-sky-600/25'
          : 'bg-violet-600/15 text-violet-700 dark:text-violet-300 border-violet-600/25'

    const label = kind === 'required' ? 'Required' : kind === 'optional' ? 'Optional' : 'Conditional'

    return (
      <span className={`inline-flex items-center gap-2 text-xs px-2 py-1 rounded-full border ${styles}`}>
        <span className="font-semibold">{label}</span>
        <span className="opacity-80">{children}</span>
      </span>
    )
  }

  const Node = ({
    title,
    note,
    kind = 'required',
    dependsOn,
  }: {
    title: string
    note?: React.ReactNode
    kind?: 'required' | 'optional' | 'conditional'
    dependsOn?: string
  }) => (
    <div className="flex items-start gap-3">
      <div className="mt-[7px] h-2 w-2 rounded-full bg-muted-foreground/60 shrink-0" />
      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-2">
          <p className="font-semibold text-foreground">{title}</p>
          {kind !== 'required' && (
            <Badge kind={kind}>{kind === 'conditional' ? `only if: ${dependsOn}` : ''}</Badge>
          )}
        </div>
        {note && <div className="text-sm text-muted-foreground">{note}</div>}
      </div>
    </div>
  )

  const SpeechCard = ({
    title,
    time,
    required,
    optional,
    conditional,
  }: {
    title: string
    time: string
    required: Array<{ title: string; note?: React.ReactNode }>
    optional?: Array<{ title: string; note?: React.ReactNode }>
    conditional?: Array<{ title: string; note?: React.ReactNode; dependsOn: string }>
  }) => (
    <section className="rounded-xl border bg-background overflow-hidden">
      <div className="px-4 py-3 bg-muted/30 border-b flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <h4 className="text-base md:text-lg font-semibold text-foreground">{title}</h4>
          <span className="text-xs px-2 py-1 rounded-full border bg-background text-muted-foreground">
            {time}
          </span>
        </div>
      </div>

      <div className="p-4 grid grid-cols-1 lg:grid-cols-12 gap-6">
        <div className="lg:col-span-6">
          <div className="flex items-center gap-2 mb-3">
            <Badge kind="required">must-have</Badge>
          </div>
          <div className="space-y-3">
            {required.map((r) => (
              <Node key={r.title} title={r.title} note={r.note} />
            ))}
          </div>
        </div>

        <div className="lg:col-span-6">
          <div className="flex items-center gap-2 mb-3 flex-wrap">
            <Badge kind="optional">nice-to-have</Badge>
            <Badge kind="conditional">depends</Badge>
          </div>
          <div className="space-y-3">
            {(optional || []).map((o) => (
              <Node key={o.title} title={o.title} note={o.note} kind="optional" />
            ))}
            {(conditional || []).map((c) => (
              <Node
                key={`${c.title}-${c.dependsOn}`}
                title={c.title}
                note={c.note}
                kind="conditional"
                dependsOn={c.dependsOn}
              />
            ))}
            {(!optional || optional.length === 0) && (!conditional || conditional.length === 0) && (
              <p className="text-sm text-muted-foreground">No optional/conditional items for this speech.</p>
            )}
          </div>
        </div>
      </div>
    </section>
  )

  return (
    <article className="text-sm md:text-base leading-7">
      <header className="space-y-2">
        <h3 className="text-xl md:text-2xl font-semibold text-foreground">LD Cookbook (Speech-by-Speech)</h3>
        <p className="text-muted-foreground">
          This is a full “cookbook” breakdown of LD speeches. It’s designed to be visual and
          practical: each line-item tells you what to write, and is tagged as{' '}
          <span className="font-semibold">Required</span>, <span className="font-semibold">Optional</span>, or{' '}
          <span className="font-semibold">Conditional</span> (only do it if that position exists in the round).
        </p>

        <div className="mt-3 flex flex-wrap gap-2">
          <Badge kind="required">always do this</Badge>
          <Badge kind="optional">add if helpful</Badge>
          <Badge kind="conditional">only if the position exists</Badge>
        </div>
      </header>

      {/* Pre-speech prep */}
      <div className="mt-6 rounded-xl border bg-muted/10 p-4">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <h4 className="text-lg font-semibold text-foreground">Pre-speech prep (the “recipe” before you write)</h4>
          <Badge kind="required">do before round</Badge>
        </div>
        <div className="mt-3 grid grid-cols-1 lg:grid-cols-12 gap-4">
          <div className="lg:col-span-7 text-muted-foreground space-y-3">
            <p>
              Do these steps in order. They determine whether your 1AC/1NC will be clear and extendable.
            </p>
            <ol className="list-decimal pl-5 space-y-2">
              <li>
                <span className="font-semibold text-foreground">Study the resolution</span>: identify key terms and
                the core moral disagreement.
              </li>
              <li>
                <span className="font-semibold text-foreground">Pick your framework first</span>: choose a{' '}
                <GlossaryTerm term="Value">Value</GlossaryTerm> and a{' '}
                <GlossaryTerm term="Criterion">Criterion</GlossaryTerm> that you can actually apply to impacts.
              </li>
              <li>
                <span className="font-semibold text-foreground">Draft 2–3 contentions</span> using a fixed template:
                Tag → <GlossaryTerm term="Warrant">Warrant(s)</GlossaryTerm> →{' '}
                <GlossaryTerm term="Impact">Impact(s)</GlossaryTerm> → Tie-back to{' '}
                <GlossaryTerm term="Criterion">Criterion</GlossaryTerm>.
              </li>
              <li>
                <span className="font-semibold text-foreground">Integrate evidence into warrants</span>: evidence is
                not a standalone “section”—it supports a specific claim.
              </li>
              <li>
                <span className="font-semibold text-foreground">Write blocks</span> (pre-written responses) for the
                most likely attacks so your 1AR/2AR aren’t improvised.
              </li>
            </ol>
          </div>
          <div className="lg:col-span-5">
            <div className="rounded-lg border bg-background p-3">
              <p className="font-semibold text-foreground">Contention template (copy/paste)</p>
              <pre className="mt-2 whitespace-pre-wrap text-xs text-muted-foreground">
{`Contention # (Tag): [1-line claim]
Warrant 1: [reason]
Warrant 2: [reason] (optional)
Impact: [why it matters]
Tie-back: [explicitly connect to criterion]`}
              </pre>
            </div>
            <div className="mt-3 rounded-lg border bg-background p-3">
              <p className="font-semibold text-foreground">Framework lines (copy/paste)</p>
              <pre className="mt-2 whitespace-pre-wrap text-xs text-muted-foreground">
{`Value: ___.
Criterion: ___.
Explanation (2–4 sentences): The judge should…`}
              </pre>
            </div>
          </div>
        </div>
      </div>

      <div className="mt-6 space-y-6">
        <SpeechCard
          title="1AC — First Affirmative Constructive"
          time="6 minutes"
          required={[
            {
              title: 'Roadmap',
              note: (
                <>
                  <p>
                    A one-sentence navigation line. Do not argue here. (This is your{' '}
                    <GlossaryTerm term="Roadmap">roadmap</GlossaryTerm>.)
                  </p>
                  <pre className="mt-2 whitespace-pre-wrap rounded-md border bg-muted/20 p-2 text-xs">
{`"I’ll begin with definitions and framework, then I’ll read two contentions."`}
                  </pre>
                </>
              ),
            },
            {
              title: 'Framework — Value (one line)',
              note: (
                <>
                  <p>Write the value as a labeled line so you can extend it later.</p>
                  <pre className="mt-2 whitespace-pre-wrap rounded-md border bg-muted/20 p-2 text-xs">{`Value: Justice.`}</pre>
                </>
              ),
            },
            {
              title: 'Framework — Criterion/Standard (one line)',
              note: (
                <>
                  <p>This is the test for your value. Make it something you can apply.</p>
                  <pre className="mt-2 whitespace-pre-wrap rounded-md border bg-muted/20 p-2 text-xs">{`Criterion: Protecting individual rights.`}</pre>
                </>
              ),
            },
            {
              title: 'Framework — Explanation (2–4 sentences)',
              note: (
                <>
                  <p>Tell the judge how to use the criterion to evaluate impacts.</p>
                  <pre className="mt-2 whitespace-pre-wrap rounded-md border bg-muted/20 p-2 text-xs">
{`"The judge should evaluate the round by asking which side better protects individual rights.
If a position violates basic rights, it cannot be morally justified under this framework."`}
                  </pre>
                </>
              ),
            },
            {
              title: 'Definitions & Observations (only what matters)',
              note: (
                <>
                  <p>Define key terms you rely on, and set burdens/scope if needed.</p>
                  <pre className="mt-2 whitespace-pre-wrap rounded-md border bg-muted/20 p-2 text-xs">
{`Definition: Civil disobedience = intentional, public, nonviolent law-breaking to protest injustice.
Observation: Because this is moral justification, the round turns on ethical evaluation under the framework.`}
                  </pre>
                </>
              ),
            },
            {
              title: 'Contentions (2–3) written line-by-line',
              note: (
                <>
                  <p>
                    Each contention should be written as short lines you can extend later. Do not hide Value/Criterion/Contentions inside paragraphs.
                  </p>
                  <pre className="mt-2 whitespace-pre-wrap rounded-md border bg-muted/20 p-2 text-xs">
{`Contention 1 (Tag): [1-line claim]
Warrant: [reason — evidence can be read here]
Impact: [why it matters morally/practically]
Tie-back: [explicitly connect to criterion]`}
                  </pre>
                  <p className="mt-2">
                    Evidence belongs inside the warrant line (support for a claim), not as a separate “section.”
                  </p>
                </>
              ),
            },
            {
              title: 'Crystallizing close (“reasons to affirm”)',
              note: (
                <>
                  <p>End by telling the judge what the ballot should say if you’re ahead.</p>
                  <pre className="mt-2 whitespace-pre-wrap rounded-md border bg-muted/20 p-2 text-xs">
{`"Under my criterion, my contentions show civil disobedience prevents serious rights violations in democracy.
Because rights take priority in the framework, you should affirm."`}
                  </pre>
                </>
              ),
            },
          ]}
          optional={[
            {
              title: 'Hook / attention step',
              note: (
                <>
                  <p>Use only if it improves clarity. Keep it under ~15 seconds.</p>
                  <pre className="mt-2 whitespace-pre-wrap rounded-md border bg-muted/20 p-2 text-xs">
{`"Imagine a law that is legal but clearly unjust—LD asks whether obeying it is still morally required."`}
                  </pre>
                </>
              ),
            },
            {
              title: 'Preemptive defense',
              note: 'Only if you can do it briefly; otherwise save time for stronger contentions.',
            },
          ]}
        />

        <SpeechCard
          title="Cross-Ex of the 1AC"
          time="3 minutes"
          required={[
            { title: 'Clarification questions', note: 'Definitions, framework, burdens, what Aff actually claims.' },
            { title: 'Setup questions', note: 'Force concessions you can use later in the 1NC/2NR.' },
            { title: 'Probing questions', note: 'Find contradictions or missing links in the Aff case.' },
            { title: 'Concession questions', note: 'Corner Aff into admissions that weaken their offense.' },
          ]}
          optional={[
            { title: 'Strategy for questioner', note: 'Don’t argue—build traps and clean yes/no answers.' },
            { title: 'Strategy for answerer', note: 'Be concise; don’t volunteer extra claims you can’t defend.' },
          ]}
        />

        <SpeechCard
          title="1NC — First Negative Constructive"
          time="7 minutes"
          required={[
            {
              title: 'Roadmap',
              note: (
                <>
                  <p>One sentence: your order (framework → contention-by-contention → off-case if any).</p>
                  <pre className="mt-2 whitespace-pre-wrap rounded-md border bg-muted/20 p-2 text-xs">
{`"I’ll start with framework, then go contention by contention on the affirmative case."`}
                  </pre>
                </>
              ),
            },
            {
              title: 'Framework clash (choose a plan and say it clearly)',
              note: (
                <>
                  <p>Pick ONE and be explicit:</p>
                  <ul className="mt-2 list-disc pl-5 space-y-1">
                    <li>
                      <span className="font-semibold text-foreground">Option A</span>: “Even under your criterion, you lose because…”
                    </li>
                    <li>
                      <span className="font-semibold text-foreground">Option B</span>: “Your criterion is wrong; prefer mine because…”
                    </li>
                  </ul>
                  <pre className="mt-2 whitespace-pre-wrap rounded-md border bg-muted/20 p-2 text-xs">
{`Option A: "Even under protecting rights, civil disobedience undermines predictable rights enforcement."
Option B: "Prefer consistent rule-of-law; predictable enforcement is necessary for rights protection."`}
                  </pre>
                </>
              ),
            },
            {
              title: 'On-case line-by-line (do it in the Aff order)',
              note: (
                <>
                  <p>For each Aff contention, write 2–4 responses using standard LD moves:</p>
                  <ol className="mt-2 list-decimal pl-5 space-y-1">
                    <li><span className="font-semibold text-foreground">No link</span> (their warrant doesn’t connect)</li>
                    <li><span className="font-semibold text-foreground">Defense</span> (even if true, impact is small / doesn’t meet criterion)</li>
                    <li><span className="font-semibold text-foreground">Turn</span> (their argument helps Neg)</li>
                    <li><span className="font-semibold text-foreground">Weighing</span> (why your impact comes first)</li>
                  </ol>
                  <pre className="mt-2 whitespace-pre-wrap rounded-md border bg-muted/20 p-2 text-xs">
{`Against Contention 1:
- No link: disobedience can provoke backlash that reduces rights protections.
- Defense: legal reform exists in democracies; disobedience isn't necessary.
- Turn: normalizing law-breaking erodes predictable enforcement, harming rights long-term.`}
                  </pre>
                </>
              ),
            },
            {
              title: 'Neg offense (your “reasons to vote Neg”)',
              note: (
                <>
                  <p>
                    Decide what your ballot path is. If you’re not running off-case, your “offense” can still be turns and weighing.
                  </p>
                  <p className="mt-2">
                    The key is: your Neg offense must be something you can extend and weigh in the 2NR.
                  </p>
                </>
              ),
            },
          ]}
          optional={[
            { title: 'Neg framework (counter-standard)', note: 'Only if it helps you win evaluation.' },
            { title: 'Overview', note: 'A short “big picture” frame before line-by-line.' },
            { title: 'Evidence highlights', note: 'Read the underlined sections clearly for dense philosophy.' },
          ]}
          conditional={[
            {
              title: 'Topicality (T) shell',
              dependsOn: 'you are reading T',
              note: (
                <>
                  <p>Write it in this order so it flows cleanly:</p>
                  <pre className="mt-2 whitespace-pre-wrap rounded-md border bg-muted/20 p-2 text-xs">
{`Interpretation → Violation → Standards → Voters`}
                  </pre>
                </>
              ),
            },
            {
              title: 'Theory shell',
              dependsOn: 'you are reading theory',
              note: (
                <>
                  <p>Keep it structured and short:</p>
                  <pre className="mt-2 whitespace-pre-wrap rounded-md border bg-muted/20 p-2 text-xs">
{`Violation → Standards (fairness/education/ground) → Voters`}
                  </pre>
                </>
              ),
            },
            {
              title: 'Kritik (K)',
              dependsOn: 'you are reading a K',
              note: (
                <>
                  <p>Only include if you’re actually running a K. The structure is:</p>
                  <pre className="mt-2 whitespace-pre-wrap rounded-md border bg-muted/20 p-2 text-xs">
{`Link → Impact → Alternative → Role of the Ballot (ROB)`}
                  </pre>
                </>
              ),
            },
            {
              title: 'Disadvantage (DA)',
              dependsOn: 'you are reading a DA',
              note: (
                <>
                  <p>Write it as a chain so it’s extendable:</p>
                  <pre className="mt-2 whitespace-pre-wrap rounded-md border bg-muted/20 p-2 text-xs">
{`Uniqueness → Link → Internal Link → Impact`}
                  </pre>
                </>
              ),
            },
            {
              title: 'Counterplan (CP)',
              dependsOn: 'you are reading a CP',
              note: (
                <>
                  <p>Keep the CP explicit:</p>
                  <pre className="mt-2 whitespace-pre-wrap rounded-md border bg-muted/20 p-2 text-xs">
{`Text → Solvency → Net Benefit`}
                  </pre>
                </>
              ),
            },
            {
              title: 'Impact turns',
              dependsOn: 'Aff impact can be flipped',
              note: 'Only do this if you can clearly explain the “flip” and weigh it under the framework.',
            },
          ]}
        />

        <SpeechCard
          title="Cross-Ex of the 1NC"
          time="3 minutes"
          required={[
            { title: 'Exposure questions', note: 'Find contradictions in Neg framework/off-case.' },
            { title: 'Link press questions', note: 'Force specifics: “What exactly links? Where?”' },
            { title: 'Scope clarification', note: 'Narrow what the Neg is really going for.' },
          ]}
          conditional={[
            {
              title: 'Theory clarification',
              dependsOn: 'Neg read T/Theory',
              note: 'Pin down standards and voters so you can answer efficiently in the 1AR.',
            },
          ]}
          optional={[
            { title: 'Strategy for questioner', note: 'Set up 1AR blocks and get clean concessions.' },
            { title: 'Strategy for answerer', note: 'Don’t overcommit; avoid giving away no-link admissions.' },
          ]}
        />

        <SpeechCard
          title="1AR — First Affirmative Rebuttal"
          time="4 minutes"
          required={[
            {
              title: 'Strategic overview (20–30 seconds)',
              note: (
                <>
                  <p>Say what matters and what you’re going for.</p>
                  <pre className="mt-2 whitespace-pre-wrap rounded-md border bg-muted/20 p-2 text-xs">
{`"This round comes down to framework and Contention 1. Under my criterion, Aff offense outweighs because..."`}
                  </pre>
                </>
              ),
            },
            {
              title: 'Answer every live Neg position (but only positions that exist)',
              note: (
                <>
                  <p>
                    If you miss a major sheet, you can lose on a drop. But do not waste time answering positions that were never read.
                  </p>
                  <ul className="mt-2 list-disc pl-5 space-y-1">
                    <li><span className="font-semibold text-foreground">No K in the round</span> → 0 K answers.</li>
                    <li><span className="font-semibold text-foreground">Neg collapses to a DA</span> → spend most time on DA answers + weighing.</li>
                    <li><span className="font-semibold text-foreground">Neg doesn’t extend a sheet</span> → you may not need to spend time there (circuit dependent).</li>
                  </ul>
                </>
              ),
            },
            {
              title: 'Rebuild the Aff case (extend framework + best contention)',
              note: (
                <>
                  <p>
                    “Rebuild” means: extend the line, re-explain the warrant, and reattach the impact under your criterion.
                  </p>
                  <pre className="mt-2 whitespace-pre-wrap rounded-md border bg-muted/20 p-2 text-xs">
{`"Extend my criterion: protecting rights controls because without rights, democracy is illegitimate.
Extend Contention 1: Neg never answers the warrant that minorities can be blocked from legal remedies; that makes disobedience necessary to prevent rights violations."`}
                  </pre>
                </>
              ),
            },
            {
              title: 'Signal collapse',
              note: 'Tell the judge what the round is now about (the few issues you want to decide the ballot).',
            },
          ]}
          conditional={[
            {
              title: 'Topicality answers',
              dependsOn: 'Neg read T',
              note: 'Counter-interpretation → standards → voters.',
            },
            {
              title: 'Theory answers',
              dependsOn: 'Neg read theory',
              note: 'Why shell fails; answer standards and voters; offense/defense as needed.',
            },
            {
              title: 'K answers: no-link / perm / impact turns',
              dependsOn: 'Neg read a K',
              note: (
                <>
                  <p>Only do this if there is a K. Write answers in clean categories:</p>
                  <pre className="mt-2 whitespace-pre-wrap rounded-md border bg-muted/20 p-2 text-xs">
{`No-link: [why Aff does not trigger the K]
Permutation: [do both / why it proves the alt is unnecessary]
Impact turns: [why their impact claim backfires]`}
                  </pre>
                </>
              ),
            },
            {
              title: 'DA answers: non-unique / no-link / impact defense / turns',
              dependsOn: 'Neg read a DA',
              note: (
                <>
                  <p>Pick 2–3 clean answers and explain them.</p>
                  <pre className="mt-2 whitespace-pre-wrap rounded-md border bg-muted/20 p-2 text-xs">
{`Non-unique: [the impact happens anyway]
No-link: [Aff does not cause the DA]
Impact defense: [impact is small/low probability]
Turns: [Aff prevents the impact]`}
                  </pre>
                </>
              ),
            },
            {
              title: 'CP answers: theory / solvency deficit / permutations',
              dependsOn: 'Neg read a CP',
              note: 'Answer competition, solvency, and net benefit.',
            },
          ]}
          optional={[
            { title: 'New evidence', note: 'Sometimes allowed, but time-limited and circuit-dependent.' },
          ]}
        />

        <SpeechCard
          title="2NR — Negative Rebuttal"
          time="6 minutes"
          required={[
            {
              title: 'Pick your collapse (before you speak)',
              note: (
                <>
                  <p>
                    The 2NR is where <GlossaryTerm term="Neg">Neg</GlossaryTerm> wins or loses. You should almost never “go for everything.”
                    Decide your path to the ballot first, then execute it cleanly.
                  </p>
                  <ul className="mt-2 list-disc pl-5 space-y-1">
                    <li>
                      Choose <span className="font-semibold text-foreground">1–2 issues</span> to win on (e.g., <GlossaryTerm term="Framework">framework</GlossaryTerm> + one contention, or a <GlossaryTerm term="DA">DA</GlossaryTerm>, or a <GlossaryTerm term="Kritik">K</GlossaryTerm>).
                    </li>
                    <li>
                      Everything you say should support those issues (extensions + weighing + voters).
                    </li>
                    <li>
                      If you “touch” too many sheets, you won’t weigh anything well.
                    </li>
                  </ul>
                </>
              ),
            },
            {
              title: 'Write an overview (45–60 seconds) that gives the judge a map',
              note: (
                <>
                  <p>
                    Your overview should tell the judge: what controls the round, what you’re going for,
                    and what decision they should write.
                  </p>
                  <pre className="mt-2 whitespace-pre-wrap rounded-md border bg-muted/20 p-2 text-xs">
{`"Judge, this round comes down to framework plus Neg offense on [X].
Even if you grant the Aff some benefits, the decisive question is [weighing claim].
Under [framework], Neg wins because [short reason]."`}
                  </pre>
                </>
              ),
            },
            {
              title: 'Extend the winning framework (or win under theirs)',
              note: (
                <>
                  <p>
                    You need to tell the judge what lens they’re using. Either:
                  </p>
                  <ul className="mt-2 list-disc pl-5 space-y-1">
                    <li>
                      <span className="font-semibold text-foreground">Win your framework</span> (preferred), or
                    </li>
                    <li>
                      <span className="font-semibold text-foreground">Win under their framework</span> (show that even if their criterion applies, Neg still wins).
                    </li>
                  </ul>
                  <pre className="mt-2 whitespace-pre-wrap rounded-md border bg-muted/20 p-2 text-xs">
{`"Prefer my standard because it is the most practical way to evaluate morality in this round…"
OR
"Even if you stay on protecting rights, our offense shows the Aff harms rights more than it protects them."`}
                  </pre>
                </>
              ),
            },
            {
              title: 'Extend your offense in the “flow order” (make it extendable)',
              note: (
                <>
                  <p>
                    Don’t just say “extend the DA.” Extend the chain and reattach the impact story.
                  </p>
                  <pre className="mt-2 whitespace-pre-wrap rounded-md border bg-muted/20 p-2 text-xs">
{`"Extend our link: [Aff action/claim] causes [mechanism].
Extend the internal link: that mechanism leads to [impact].
Impact: [why it matters] and it outweighs because…"`}
                  </pre>
                  <p className="mt-2">
                    If you’re going for on-case turns, extend them the same way: claim → warrant → impact → tie-back.
                  </p>
                </>
              ),
            },
            {
              title: 'Impact calculus (weighing) — say it explicitly',
              note: (
                <>
                  <p>
                    This is where many 2NRs fail. You must compare, not just assert.
                    Use the same categories every time:
                  </p>
                  <ul className="mt-2 list-disc pl-5 space-y-1">
                    <li><span className="font-semibold text-foreground">Magnitude</span>: how big?</li>
                    <li><span className="font-semibold text-foreground">Probability</span>: how likely?</li>
                    <li><span className="font-semibold text-foreground">Timeframe</span>: how soon?</li>
                    <li><span className="font-semibold text-foreground">Reversibility / moral priority</span>: can it be undone / does it come first?</li>
                  </ul>
                  <pre className="mt-2 whitespace-pre-wrap rounded-md border bg-muted/20 p-2 text-xs">
{`"Even if Aff has some risk, Neg comes first on probability and timeframe.
Aff relies on speculation; our impact is immediate and directly tied to the framework.
On moral priority, [rights / legitimacy / autonomy] outweighs [their impact]."`}
                  </pre>
                </>
              ),
            },
            {
              title: 'Crystallization: 2–3 voting issues (the ballot reasons)',
              note: (
                <>
                  <p>
                    End with short, numbered voters. Make them so clear the judge can copy them into the RFD.
                  </p>
                  <pre className="mt-2 whitespace-pre-wrap rounded-md border bg-muted/20 p-2 text-xs">
{`Voting issues:
1) Framework: [why Neg framework controls OR why Neg wins under Aff framework]
2) Offense: [your winning argument and why it survived]
3) Weighing: [the comparison that makes your offense come first]`}
                  </pre>
                </>
              ),
            },
            {
              title: 'Rhetorical close (one sentence)',
              note: (
                <pre className="mt-2 whitespace-pre-wrap rounded-md border bg-muted/20 p-2 text-xs">
{`"For those reasons, Neg wins the framework and the most important offense—so you should negate."`}
                </pre>
              ),
            },
          ]}
          conditional={[
            {
              title: 'Extend T/Theory/K/DA/CP',
              dependsOn: 'those positions are in the round',
              note: 'Only extend what you actually intend to win on.',
            },
            {
              title: 'On-case final attacks',
              dependsOn: 'your 2NR includes on-case offense/defense',
              note: 'Tie back to framework and weighing.',
            },
          ]}
          optional={[
            {
              title: 'New evidence',
              note: 'Some circuits allow limited new evidence; many judges dislike it. Only use if necessary and permitted.',
            },
          ]}
        />

        <SpeechCard
          title="2AR — Second Affirmative Rebuttal"
          time="3 minutes"
          required={[
            {
              title: 'Start with the ballot story (10–20 seconds)',
              note: (
                <>
                  <p>
                    The 2AR is “decision-writing.” Begin with the judge’s job in one sentence: what lens, what issue, what outcome.
                  </p>
                  <pre className="mt-2 whitespace-pre-wrap rounded-md border bg-muted/20 p-2 text-xs">
{`"Judge, vote Aff under [our framework]. The round comes down to [key issue], and Aff wins that issue on [weighing]."`}
                  </pre>
                </>
              ),
            },
            {
              title: 'Answer what Neg collapsed to (first)',
              note: (
                <>
                  <p>
                    You must take out the 2NR’s best offense. Don’t start by extending your favorite contention if Neg is winning elsewhere.
                  </p>
                  <ul className="mt-2 list-disc pl-5 space-y-1">
                    <li>If Neg collapses to a DA → answer link/internal link/impact and weigh.</li>
                    <li>If Neg collapses to framework → win the framework or win under theirs.</li>
                    <li>If Neg collapses to one on-case turn → answer that turn first.</li>
                  </ul>
                </>
              ),
            },
            {
              title: 'Rebuild your best offense (selective extensions only)',
              note: (
                <>
                  <p>
                    You don’t have time to extend everything. Extend the argument(s) that actually win the ballot and explain why they’re still true.
                  </p>
                  <pre className="mt-2 whitespace-pre-wrap rounded-md border bg-muted/20 p-2 text-xs">
{`"Extend Contention 1: [tag]. The warrant stands because [answer to Neg response].
Impact: [why it matters]. Tie-back: under the criterion, this directly satisfies the standard."`}
                  </pre>
                </>
              ),
            },
            {
              title: 'Impact calculus (weighing) — make it unavoidable',
              note: (
                <>
                  <p>
                    Don’t just list weighing words—apply them to both sides and conclude.
                  </p>
                  <pre className="mt-2 whitespace-pre-wrap rounded-md border bg-muted/20 p-2 text-xs">
{`"On probability, Aff is higher because [reason].
On timeframe, Aff is faster because [reason].
On moral priority under the framework, [rights/autonomy/etc.] comes first.
Therefore even if Neg has some risk, Aff outweighs."`}
                  </pre>
                </>
              ),
            },
            {
              title: 'Final voters (2–3 short reasons to affirm)',
              note: (
                <>
                  <p>End with voters the judge can copy into the RFD.</p>
                  <pre className="mt-2 whitespace-pre-wrap rounded-md border bg-muted/20 p-2 text-xs">
{`Vote Aff because:
1) Framework: [why our lens controls]
2) Offense: [our winning argument survives]
3) Weighing: [why our impact comes first]`}
                  </pre>
                </>
              ),
            },
          ]}
          conditional={[
            {
              title: 'Answer the 2NR’s chosen sheet(s)',
              dependsOn: 'whatever Neg collapsed to',
              note: (
                <>
                  <p>
                    Your speaking order should match the 2NR’s collapse. If Neg goes for one issue, you go there immediately.
                  </p>
                  <p className="mt-2">
                    Then you extend the Aff offense that wins <span className="font-semibold text-foreground">under the winning framework</span>.
                  </p>
                </>
              ),
            },
          ]}
          optional={[
            {
              title: 'Highlight new evidence',
              note: 'Use sparingly (and only if allowed). In most rounds, explanation + weighing matters more than adding new cards.',
            },
          ]}
        />
      </div>

      <section className="mt-10 grid grid-cols-1 lg:grid-cols-12 gap-6">
        <div className="lg:col-span-7 rounded-xl border bg-background p-4">
          <h4 className="text-lg font-semibold text-foreground">Time management (quick allocations)</h4>
          <p className="mt-1 text-muted-foreground">
            These are practical targets to keep you organized. Adjust for your style and your circuit.
          </p>
          <div className="mt-4 overflow-x-auto rounded-lg border">
            <table className="w-full text-sm">
              <thead className="bg-muted/40">
                <tr className="text-left">
                  <th className="px-3 py-2 font-semibold text-foreground">Speech</th>
                  <th className="px-3 py-2 font-semibold text-foreground">Suggested allocation</th>
                </tr>
              </thead>
              <tbody className="divide-y text-muted-foreground">
                <tr>
                  <td className="px-3 py-2 font-semibold text-foreground">1AC</td>
                  <td className="px-3 py-2">Framework/Defs 1–1.5 min · Contentions 3–4 min · Close ~30 sec</td>
                </tr>
                <tr>
                  <td className="px-3 py-2 font-semibold text-foreground">1NC</td>
                  <td className="px-3 py-2">Off-case 3–4 min · On-case 2–3 min · Overview ~30 sec</td>
                </tr>
                <tr>
                  <td className="px-3 py-2 font-semibold text-foreground">1AR</td>
                  <td className="px-3 py-2">Overview 20–30 sec · Off-case 2–2.5 min · Rebuild 1–1.5 min</td>
                </tr>
                <tr>
                  <td className="px-3 py-2 font-semibold text-foreground">2NR</td>
                  <td className="px-3 py-2">Overview 45–60 sec · Extend off-case 2–3 min · Clash 1–2 min · Voters ~1 min</td>
                </tr>
                <tr>
                  <td className="px-3 py-2 font-semibold text-foreground">2AR</td>
                  <td className="px-3 py-2">Overview ~30 sec · Refutations 1–1.5 min · Extensions 45–60 sec · Voters ~30 sec</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <div className="lg:col-span-5 rounded-xl border bg-background p-4">
          <h4 className="text-lg font-semibold text-foreground">Strategic decisions (when to use what)</h4>
          <p className="mt-1 text-muted-foreground">
            Use optional tools when they solve a real problem—don’t add complexity “just because.”
          </p>

          <div className="mt-4 space-y-4">
            <div className="rounded-lg border bg-muted/20 p-4">
              <p className="font-semibold text-foreground">When to include optional elements</p>
              <div className="mt-2 space-y-2 text-muted-foreground">
                <Node title="Hook" note="Use when you need quick moral grounding or clarity." kind="optional" />
                <Node
                  title="Philosophical justification"
                  note="Especially helpful when the framework is complex or contested."
                  kind="optional"
                />
                <Node title="Highlights" note="Use for dense philosophy/evidence to keep clarity." kind="optional" />
                <Node
                  title="Neg framework"
                  note="Use when Aff framework is weak or strategically vulnerable."
                  kind="optional"
                />
                <Node title="Overview" note="Use when the judge needs direction on prioritization." kind="optional" />
              </div>
            </div>

            <div className="rounded-lg border bg-muted/20 p-4">
              <p className="font-semibold text-foreground">Collapse vs extend</p>
              <div className="mt-2 space-y-2 text-muted-foreground">
                <Node
                  title="Collapse"
                  note="Best when one path clearly wins. Makes late speeches cleaner and more persuasive."
                  kind="required"
                />
                <Node
                  title="Extend everything"
                  note="More common early or if the judge is not flowing reliably—but it’s risky late."
                  kind="optional"
                />
              </div>
            </div>

            <div className="rounded-lg border bg-muted/20 p-4">
              <p className="font-semibold text-foreground">What to emphasize</p>
              <div className="mt-2 space-y-2 text-muted-foreground">
                <Node
                  title="Off-case"
                  note="When Neg has strong procedural or critical positions that can decide the ballot."
                  kind="conditional"
                  dependsOn="there is meaningful off-case"
                />
                <Node
                  title="On-case"
                  note="When the Aff is internally flawed and you can win directly on their case."
                  kind="required"
                />
                <Node
                  title="Framework"
                  note="When the philosophical lens determines which impacts matter at all."
                  kind="required"
                />
              </div>
            </div>
          </div>
        </div>
      </section>
    </article>
  )
}

export default function DebateElementsCourse() {
  const { currentOrg, appUserLoaded } = useAuth()
  const isEligible = appUserLoaded && !!currentOrg

  const steps = useMemo(
    () => [
      {
        title: 'History of Debate',
        content: <HistoryOfDebateArticle />,
      },
      {
        title: 'Modern Debate',
        content: <ModernDebateArticle />,
      },
      {
        title: 'Lincoln–Douglas Debate',
        content: <LincolnDouglasArticle />,
      },
      {
        title: 'LD Cookbook',
        content: <LDElementsArticle />,
      },
    ],
    [],
  )
  const [stepIndex, setStepIndex] = useState(0)
  const totalSteps = steps.length
  const step = steps[stepIndex]

  const canGoBack = stepIndex > 0
  const canGoNext = stepIndex < totalSteps - 1

  return (
    <Layout>
      <div className="w-full px-4 py-6 md:px-6 lg:px-0 lg:w-3/5 xl:w-[60%]">
        <SignedIn>
          {!isEligible ? (
            <div className="flex flex-col gap-3">
              <h1 className="text-2xl font-bold">Debate Elements Course</h1>
              <p className="text-sm text-muted-foreground">
                Available for paid plans only. Please purchase a plan to access this course.
              </p>
              <div className="flex gap-2 flex-wrap">
                <Link to="/paddle-checkout">
                  <Button variant="outline" disabled={!appUserLoaded}>
                    Purchase a plan now
                  </Button>
                </Link>
                <Link to="/learning-toolkit">
                  <Button variant="outline">Back to Learning &amp; Toolkit</Button>
                </Link>
              </div>
            </div>
          ) : (
            <div className="flex flex-col gap-6">
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                  <h1 className="text-2xl font-bold">Debate Elements Course</h1>
                  <p className="text-sm text-muted-foreground">
                    Step {stepIndex + 1} of {totalSteps}
                  </p>
                </div>
                <Link to="/learning-toolkit">
                  <Button variant="outline">Back to Learning &amp; Toolkit</Button>
                </Link>
              </div>

              {/* Stepper */}
              <div className="flex items-center gap-2 flex-wrap">
                {steps.map((s, idx) => {
                  const active = idx === stepIndex
                  const done = idx < stepIndex
                  const isLast = idx === steps.length - 1
                  return (
                    <div key={s.title} className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => setStepIndex(idx)}
                        className={[
                          'px-3 py-2 rounded-md text-sm border transition',
                          active
                            ? 'bg-violet-700 text-white border-violet-700'
                            : done
                              ? 'bg-muted border-border'
                              : 'bg-background border-border hover:bg-muted',
                        ].join(' ')}
                      >
                        {idx + 1}. {s.title}
                      </button>
                      {!isLast && <span className="text-muted-foreground select-none">→</span>}
                    </div>
                  )
                })}
              </div>

              {/* Lesson body */}
              <div className="border rounded-lg p-4 bg-background">
                <h2 className="text-xl font-semibold">{step.title}</h2>
                <div className="mt-3 text-muted-foreground">
                  {typeof step.content === 'string' ? <p>{step.content}</p> : step.content}
                </div>
              </div>

              {/* Controls */}
              <div className="flex items-center justify-between">
                <Button variant="outline" onClick={() => setStepIndex((i) => i - 1)} disabled={!canGoBack}>
                  Back
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setStepIndex((i) => i + 1)}
                  disabled={!canGoNext}
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </SignedIn>

        <SignedOut>
          <div className="flex flex-col gap-3">
            <h1 className="text-2xl font-bold">Debate Elements Course</h1>
            <p className="text-sm text-muted-foreground">Please sign in to access this page.</p>
            <a href="/sign-in">
              <Button variant="outline">Sign in</Button>
            </a>
          </div>
        </SignedOut>
      </div>
    </Layout>
  )
}

